import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wallet, Plus, Search, ArrowLeft, Calendar, DollarSign, 
  TrendingUp, TrendingDown, FileText, X, Trash2, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { pettyCashAPI } from '../services/api';

interface PettyCashTransaction {
  id: string;
  date: string;
  provider: string;
  concept: string;
  amount: number;
  type: 'income' | 'expense';
  receipt?: string;
  registeredBy: string;
}

// Mock data removed — page fetches real data from pettyCashAPI

export default function CajaChica() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<PettyCashTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [showNewModal, setShowNewModal] = useState(false);
  const [filter, setFilter] = useState<'all' | 'income' | 'expense'>('all');

  const { isAdmin } = useAuth();

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const response = await pettyCashAPI.getAll(condoId);
      // APIs may return an array, {data: array}, or a paginated envelope.
      const payload = response?.data?.data ?? response?.data ?? response;
      const rows = Array.isArray(payload) ? payload : payload && typeof payload === 'object'
        ? (Array.isArray(payload.items) ? payload.items : payload.transactions) : [];
      setTransactions(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(t => {
    const tDate = new Date(t.date);
    if (tDate.getMonth() !== selectedMonth || tDate.getFullYear() !== selectedYear) return false;
    if (filter !== 'all' && t.type !== filter) return false;
    return true;
  });

  const totals = filteredTransactions.reduce(
    (acc, t) => {
      if (t.type === 'income') acc.income += t.amount;
      else acc.expense += t.amount;
      return acc;
    },
    { income: 0, expense: 0 }
  );

  const openingBalance = 3000; // Should come from previous month
  const currentBalance = openingBalance + totals.income - totals.expense;

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const handleDelete = (id: string) => {
    if (confirm('¿Eliminar este movimiento?')) {
      setTransactions(prev => prev.filter(t => t.id !== id));
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748]">Caja Chica</h1>
              <p className="text-xs text-gray-500">Control de gastos menores</p>
            </div>
          </div>
          {isAdmin() && (
            <button 
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Nuevo Movimiento
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        {/* Month Selector */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#36CCD8]" />
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-4 py-2 border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              >
                {monthNames.map((name, idx) => (
                  <option key={idx} value={idx}>{name}</option>
                ))}
              </select>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
              </select>
            </div>
            <div className="flex gap-2 ml-auto">
              <button 
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${filter === 'all' ? 'bg-[#36CCD8] text-white' : 'bg-[var(--v-surface-2)] text-gray-600'}`}
              >
                Todos
              </button>
              <button 
                onClick={() => setFilter('income')}
                className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${filter === 'income' ? 'bg-emerald-500 text-white' : 'bg-[var(--v-surface-2)] text-gray-600'}`}
              >
                Ingresos
              </button>
              <button 
                onClick={() => setFilter('expense')}
                className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${filter === 'expense' ? 'bg-red-500 text-white' : 'bg-[var(--v-surface-2)] text-gray-600'}`}
              >
                Egresos
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[var(--v-surface-1)] rounded-2xl p-5 border border-[var(--v-border-light)] shadow-sm">
            <p className="text-sm text-gray-500 mb-1">Saldo Inicial</p>
            <p className="text-2xl font-bold text-[#2D3748]">${openingBalance.toLocaleString()}</p>
          </div>
          <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
            <p className="text-sm text-emerald-600 mb-1">Ingresos</p>
            <p className="text-2xl font-bold text-emerald-600">+${totals.income.toLocaleString()}</p>
          </div>
          <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
            <p className="text-sm text-red-600 mb-1">Egresos</p>
            <p className="text-2xl font-bold text-red-600">-${totals.expense.toLocaleString()}</p>
          </div>
          <div className="bg-[#36CCD8]/10 rounded-2xl p-5 border border-[#36CCD8]/30">
            <p className="text-sm text-[#36CCD8] mb-1">Saldo Actual</p>
            <p className="text-2xl font-bold text-[#36CCD8]">${currentBalance.toLocaleString()}</p>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--v-border-light)] flex items-center justify-between">
            <h3 className="font-bold text-[#2D3748]">Movimientos</h3>
            <button className="text-sm text-[#36CCD8] font-bold flex items-center gap-1">
              <Download className="w-4 h-4" /> Exportar
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {filteredTransactions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay movimientos este mes</p>
              </div>
            ) : (
              filteredTransactions.map(transaction => (
                <div key={transaction.id} className="p-4 hover:bg-[var(--v-surface-2)] transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        transaction.type === 'income' ? 'bg-emerald-100' : 'bg-red-100'
                      }`}>
                        {transaction.type === 'income' ? (
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-[#2D3748]">{transaction.concept}</p>
                        <p className="text-sm text-gray-500">{transaction.provider}</p>
                        <p className="text-xs text-gray-400">{new Date(transaction.date).toLocaleDateString('es-MX')} • {transaction.registeredBy}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className={`text-lg font-bold ${transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toLocaleString()}
                      </p>
                      {isAdmin() && (
                        <button 
                          onClick={() => handleDelete(transaction.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* New Transaction Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#2D3748] mb-6">Nuevo Movimiento</h3>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Tipo</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="type" value="expense" defaultChecked className="text-[#36CCD8]" />
                    <span>Egreso</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="type" value="income" className="text-[#36CCD8]" />
                    <span>Ingreso</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Fecha</label>
                <input type="date" className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Proveedor/Origen</label>
                <input type="text" className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none" placeholder="Ej: Ferretería Local" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Concepto</label>
                <input type="text" className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none" placeholder="Descripción del gasto" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Monto</label>
                <input type="number" className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Comprobante (opcional)</label>
                <input type="file" accept="image/*,.pdf" className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl" />
              </div>
              <div className="flex gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 py-3 border border-[var(--v-border-light)] text-gray-700 font-bold rounded-xl hover:bg-[var(--v-surface-2)]"
                >
                  Cancelar
                </button>
                <button 
                  type="button"
                  onClick={() => { setShowNewModal(false); alert('✅ Movimiento registrado'); }}
                  className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4]"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
